/**
 * @fileoverview UnifiedImportWizardPage (system-web) — canonical entry point for
 *   import template authoring backed by the shared `@firesquirrel/import-editor`
 *   package.
 * @module system-web/pages
 *
 * Responsibilities:
 * - Handle the two template-authoring intents: create-template (3-card gate) and
 *   edit-template (tabbed editor).
 * - Read URL query-string state (?mode=, ?runTenantId=, ?runTenantName=) to
 *   pre-select wizard mode and park tenant context for save-and-import handoff.
 * - Slot a system-only Visibility tab after Review via renderExtraTab.
 * - Build the system-web services adapter and pass it to UnifiedImportWizard.
 *
 * Dependencies:
 * - `UnifiedImportWizard` from `@firesquirrel/import-editor`.
 * - `buildSystemImportWizardServices` — the system-web services adapter.
 * - `getImportTemplate` / `saveDraftImportTemplate` from systemImportTemplateService.
 * - `listTenants` from tenantService — for the Card 3 tenant picker.
 * - `VisibilityTabContent` — system-only visibility tab component.
 *
 * Architecture:
 * - Presentation-only composition; all business logic lives in the shared wizard
 *   and the services adapter. No direct API calls.
 * - The parked tenant is NEVER written to the template — it is only used to kick
 *   off the run wizard after save succeeds (templates are tenant-agnostic).
 *
 * Notes:
 * - Routes: /import-templates/new → create-template; /import-templates/:id/edit
 *   → edit-template.
 * - System-only extras: Visibility tab slotted after Review; service adapter is
 *   tenant-agnostic (uses proxy-tenant endpoints on system-api).
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  UnifiedImportWizard,
  type UnifiedWizardIntent,
  type ImportEntryTenantOption,
  stateToTemplate,
} from '@firesquirrel/import-editor';
import type { ImportTemplateSampleBlob, ImportTemplateSamplePreview } from '@firesquirrel/local-db';
import { buildSystemImportWizardServices } from '../components/unifiedWizardAdapter';
import { getImportTemplate } from '../services/systemImportTemplateService';
import { saveDraftImportTemplate } from '../services/systemImportTemplateService';
import { listTenants } from '../services/tenantService';
import VisibilityTabContent, {
  validateVisibilityTab,
} from '../components/VisibilityTabContent';

const VISIBILITY_TAB_ID = 'visibility';

/**
 * @component UnifiedImportWizardPreviewPage
 * Purpose:     Canonical entry point for import template authoring in system-web,
 *              handling create-template and edit-template intents via the shared
 *              UnifiedImportWizard, with a system-only Visibility tab.
 * Props:       (none — route component; reads templateId from URL params)
 * Behavior:    Resolves intent from URL (new vs edit), reads query-string mode and
 *              tenant parking state, builds the system services adapter once per
 *              mount, slots the Visibility tab, and renders UnifiedImportWizard.
 * Data Source: local (Dexie via the services adapter and systemImportTemplateService)
 * Constraints: no direct API calls; all data flows through the services adapter
 * Performance: services and tenant list built once per mount via useMemo
 */
export default function UnifiedImportWizardPreviewPage() {
  const { id: templateId } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const isNew = !templateId || templateId === 'new';
  const intent: UnifiedWizardIntent = isNew ? 'create-template' : 'edit-template';

  // URL-driven wizard state. These are refresh-safe: a user who lands
  // on `/import-templates/new?mode=bound&runTenantId=X&runTenantName=Y`
  // sees the editor open directly in bound mode with the Card 3 save-
  // and-import button wired up.
  const urlMode = searchParams.get('mode');
  const initialMode =
    urlMode === 'shell' || urlMode === 'bound' ? urlMode : undefined;
  const runTenantId = searchParams.get('runTenantId') || undefined;
  const runTenantName = searchParams.get('runTenantName') || undefined;

  useEffect(() => {
    if (isNew || !templateId) return;
    if (runTenantId) return;

    let cancelled = false;

    getImportTemplate(templateId)
      .then((template) => {
        const resume = template?.draftResume;
        if (cancelled || !resume?.runTenantId) return;
        const restoredMode = resume.mode ?? 'bound';
        const restoredRunTenantId = resume.runTenantId;
        const restoredRunTenantName = resume.runTenantName;

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (!next.get('mode')) {
            next.set('mode', restoredMode);
          }
          next.set('runTenantId', restoredRunTenantId);
          if (restoredRunTenantName) {
            next.set('runTenantName', restoredRunTenantName);
          }
          return next;
        }, { replace: true });
      })
      .catch((err) => {
        console.warn('[UnifiedImportWizardPreviewPage] Failed to initialize URL params:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [isNew, runTenantId, setSearchParams, templateId]);

  // Services are tenant-agnostic at authoring time — parse and suggest
  // go through the system-api proxy-tenant endpoints. Build once per
  // mount so the wizard gets a stable reference.
  const services = useMemo(
    () => buildSystemImportWizardServices({ tenantId: undefined }),
    [],
  );

  // Tenant options feed Card 3 of the ImportEntryGate. Loaded once for
  // new-template flows; edit-template skips the gate entirely so the
  // fetch is deferred in that case.
  const [tenantOptions, setTenantOptions] = useState<ImportEntryTenantOption[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(isNew);
  useEffect(() => {
    if (!isNew) return;
    let cancelled = false;
    listTenants()
      .then((rows: any[]) => {
        if (cancelled) return;
        const opts: ImportEntryTenantOption[] = rows
          .map(t => ({ id: String(t._id), name: t.name || t.slug || String(t._id) }))
          .filter(t => !!t.id)
          .sort((a, b) => a.name.localeCompare(b.name));
        setTenantOptions(opts);
      })
      .catch(() => { if (!cancelled) setTenantOptions([]); })
      .finally(() => { if (!cancelled) setTenantsLoading(false); });
    return () => { cancelled = true; };
  }, [isNew]);

  return (
    <UnifiedImportWizard
      services={services}
      intent={intent}
      templateId={isNew ? undefined : templateId}
      scope="system"
      initialMode={initialMode}
      runAfterSave={
        runTenantId
          ? { tenantId: runTenantId, tenantName: runTenantName }
          : undefined
      }
      onAutosaveDraft={s => {
        if (!s._id) return;
        const patch = stateToTemplate({
          ...s,
          runTenantId: runTenantId ?? s.runTenantId,
          runTenantName: runTenantName ?? s.runTenantName,
        }, 'draft');
        saveDraftImportTemplate(s._id, patch);
      }}
      onDone={() => navigate('/import-templates')}
      onCancel={() => navigate('/import-templates')}
      // Card 3 save-and-import follow-on: after the new template is
      // persisted, jump straight into the run wizard with the saved
      // template + parked tenant + the same File the user just used
      // for authoring. No re-upload, no context loss.
      onSaveAndRun={(savedTemplateId, tenantId, sampleFile, sampleBlob, samplePreview) => {
        // Hand the File off through router history state. Files are
        // structured-cloneable so they survive the navigation intact,
        // sparing the user a second upload. Safari / Firefox / Chrome
        // all implement this per the History API spec.
        const state: {
          prefilledFile?: File;
          prefilledSampleBlob?: ImportTemplateSampleBlob;
          prefilledSamplePreview?: ImportTemplateSamplePreview;
        } = {};
        if (sampleFile) {
          state.prefilledFile = sampleFile;
        }
        if (sampleBlob) {
          state.prefilledSampleBlob = sampleBlob;
        }
        if (samplePreview) {
          state.prefilledSamplePreview = samplePreview;
        }
        navigate(
          `/import-runs/new/${encodeURIComponent(savedTemplateId)}?tenantId=${encodeURIComponent(tenantId)}`,
          Object.keys(state).length > 0 ? { state } : undefined,
        );
      }}
      // Inject the system-only Visibility tab before Review so the
      // publish step is the actual final confirmation in the flow.
      extraTabs={[{ id: VISIBILITY_TAB_ID, label: 'Visibility' }]}
      insertExtraTabsAfter="geocoding"
      renderExtraTab={(tabId, { state, updateState }) => {
        if (tabId !== VISIBILITY_TAB_ID) return null;
        return <VisibilityTabContent state={state} onChange={updateState} />;
      }}
      extraTabErrors={(tabId, state) => {
        if (tabId !== VISIBILITY_TAB_ID) return [];
        return validateVisibilityTab(state);
      }}
      // 3-card entry gate. Card 3 ("Build a full template from a file
      // and import") parks the chosen tenant in the URL so a refresh
      // persists the session; the wizard itself transitions into
      // bound-template authoring internally.
      importEntryGate={{
        tenantOptions,
        tenantsLoading,
        onChooseTemplateMode: (mode) => {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('mode', mode);
            next.delete('runTenantId');
            next.delete('runTenantName');
            return next;
          }, { replace: true });
        },
        onChooseImport: (tenantId) => {
          const tenant = tenantOptions.find(t => t.id === tenantId);
          setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('mode', 'bound');
            next.set('runTenantId', tenantId);
            if (tenant?.name) next.set('runTenantName', tenant.name);
            return next;
          }, { replace: true });
        },
      }}
    />
  );
}
